import { useEffect, useState, useCallback } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { ArrowLeft, FileText, Send, MessageCircle, IndianRupee, Copy, Edit, Ban, Trash2, ExternalLink, Link2, Check, Clock, ArrowRight, Printer, Smartphone, Bluetooth, Loader2 } from 'lucide-react';
import { api, fmtMoney, fmtDate } from '../../lib/api';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { toast } from 'sonner';
import { StatusBadge } from './InvoicingDashboard';

function todayIso() { return new Date().toISOString().slice(0, 10); }

export default function InvoiceDetailPage() {
    const { id } = useParams();
    const nav = useNavigate();
    const [inv, setInv] = useState(null);
    const [loading, setLoading] = useState(true);
    const [thermalOpen, setThermalOpen] = useState(false);
    const [payments, setPayments] = useState([]);
    const [showPay, setShowPay] = useState(false);
    const [showCancel, setShowCancel] = useState(false);
    const [cancelReason, setCancelReason] = useState('');
    const [payForm, setPayForm] = useState({ amount: 0, payment_date: todayIso(), mode: 'upi', reference: '', notes: '' });

    // Sprint 2026-07: sharing state
    const [showEmail, setShowEmail] = useState(false);
    const [emailForm, setEmailForm] = useState({ to: '', cc: '', subject: '', body: '' });
    const [sendingEmail, setSendingEmail] = useState(false);
    const [genPayLink, setGenPayLink] = useState(false);

    const load = useCallback(async () => {
        try {
            setLoading(true);
            const [i, p] = await Promise.all([
                api.invGetInvoice(id),
                api.invListPayments({ invoice_id: id }),
            ]);
            setInv(i);
            setPayments(p.items || []);
            setPayForm((f) => ({ ...f, amount: i.amount_due || 0 }));
        } catch (e) {
            toast.error('Failed to load invoice');
        } finally {
            setLoading(false);
        }
    }, [id]);

    useEffect(() => { load(); }, [load]);

    const onIssue = async () => {
        if (!confirm('Issue this invoice? A permanent invoice number will be assigned and edits will be locked.')) return;
        try {
            await api.invIssueInvoice(id);
            toast.success('Invoice issued');
            load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || 'Issue failed');
        }
    };

    const onCancel = async () => {
        try {
            await api.invCancelInvoice(id, cancelReason);
            toast.success('Invoice cancelled');
            setShowCancel(false);
            setCancelReason('');
            load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || 'Cancel failed');
        }
    };

    const onDelete = async () => {
        if (!confirm('Delete this draft? This cannot be undone.')) return;
        try {
            await api.invDeleteInvoice(id);
            toast.success('Draft deleted');
            nav('/invoicing/invoices');
        } catch (e) {
            toast.error(e?.response?.data?.detail || 'Delete failed');
        }
    };

    const onDuplicate = async () => {
        try {
            const dup = await api.invDuplicateInvoice(id);
            toast.success('Duplicated as new draft');
            nav(`/invoicing/invoices/${dup.id}`);
        } catch (e) {
            toast.error(e?.response?.data?.detail || 'Duplicate failed');
        }
    };

    const onRecordPayment = async () => {
        if (!payForm.amount || Number(payForm.amount) <= 0) { toast.error('Enter a payment amount'); return; }
        try {
            await api.invCreatePayment({
                customer_id: inv.customer_id,
                invoice_id: id,
                amount: Number(payForm.amount),
                payment_date: payForm.payment_date,
                mode: payForm.mode,
                reference: payForm.reference || null,
                notes: payForm.notes || null,
            });
            toast.success('Payment recorded');
            setShowPay(false);
            setPayForm({ amount: 0, payment_date: todayIso(), mode: 'upi', reference: '', notes: '' });
            load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || 'Payment failed');
        }
    };

    const onDeletePayment = async (pid) => {
        if (!confirm('Delete this payment record? Invoice status will update accordingly.')) return;
        try {
            await api.invDeletePayment(pid);
            toast.success('Payment removed');
            load();
        } catch (e) {
            toast.error('Delete failed');
        }
    };

    const openPdf = () => {
        // Use the centralized API client so the Authorization header is attached
        // automatically. This avoids reading the raw token from localStorage in
        // component code (auth surface is contained in lib/api.js).
        api.invInvoicePdfBlob(id)
            .then((blob) => {
                const objUrl = URL.createObjectURL(blob);
                window.open(objUrl, '_blank');
                setTimeout(() => URL.revokeObjectURL(objUrl), 60000);
            })
            .catch(() => toast.error('Failed to open PDF'));
    };

    const shareWhatsapp = () => {
        const phone = (inv?.customer_snapshot?.phone || '').replace(/[^0-9]/g, '');
        const amount = fmtMoney(inv.grand_total);
        const msg = `Hi ${inv.customer_snapshot?.name || ''}, please find Invoice ${inv.invoice_number || ''} for ${amount} due on ${fmtDate(inv.due_date)}. Thanks!`;
        const url = phone
            ? `https://wa.me/${phone}?text=${encodeURIComponent(msg)}`
            : `https://wa.me/?text=${encodeURIComponent(msg)}`;
        window.open(url, '_blank');
    };

    // Sprint 2026-07: open the "Send via email" dialog with pre-filled fields.
    // The actual send is a POST to /invoicing/invoices/{id}/email which pipes
    // the invoice PDF through the workspace's SMTP config.
    const openEmailDialog = () => {
        setEmailForm({
            to: inv?.customer_snapshot?.email || '',
            cc: '',
            subject: `Invoice ${inv?.invoice_number || ''}`,
            body: `Hi ${inv?.customer_snapshot?.name || ''},\n\nPlease find attached Invoice ${inv?.invoice_number || ''} for ${fmtMoney(inv.grand_total)} due on ${fmtDate(inv.due_date)}.\n\nRegards`,
        });
        setShowEmail(true);
    };

    const sendEmail = async () => {
        if (!emailForm.to || !emailForm.to.includes('@')) {
            toast.error('Please enter a valid recipient email.');
            return;
        }
        setSendingEmail(true);
        try {
            await api.invEmailInvoice(id, emailForm);
            toast.success(`Email sent to ${emailForm.to}`);
            setShowEmail(false);
            load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || 'Email failed');
        } finally {
            setSendingEmail(false);
        }
    };

    const copyPayLink = async () => {
        setGenPayLink(true);
        try {
            const res = await api.invCreatePaymentLink(id);
            await navigator.clipboard.writeText(res.short_url).catch(() => {});
            toast.success(res.cached ? 'Payment link copied' : 'Payment link created & copied to clipboard');
            load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || 'Could not create payment link');
        } finally {
            setGenPayLink(false);
        }
    };

    // Sprint 2: quote → invoice conversion. Creates a new draft invoice
    // pre-populated with this quote's line items and cross-links both.
    const [converting, setConverting] = useState(false);
    const convertQuote = async () => {
        setConverting(true);
        try {
            const newInv = await api.invConvertQuote(id);
            toast.success(`Draft invoice created from quote`);
            // Send the user to the new invoice so they can review + issue it.
            nav(`/invoicing/invoices/${newInv.id}`);
        } catch (e) {
            toast.error(e?.response?.data?.detail || 'Conversion failed');
        } finally {
            setConverting(false);
        }
    };

    if (loading) return <div className="space-y-3"><Skeleton className="h-8 w-64" /><Skeleton className="h-64 w-full" /></div>;
    if (!inv) return <div>Invoice not found.</div>;

    const isDraft = inv.status === 'draft';
    const isCancelled = inv.status === 'cancelled';

    return (
        <div className="space-y-4" data-testid="invoice-detail-page">
            {/* Header */}
            <div className="flex items-center justify-between gap-2 flex-wrap">
                <div className="flex items-center gap-2">
                    <Button variant="ghost" size="sm" onClick={() => nav('/invoicing/invoices')} data-testid="btn-back"><ArrowLeft size={16} /></Button>
                    <div>
                        <h1 className="text-2xl font-heading font-semibold tracking-tight flex items-center gap-3">
                            {inv.invoice_number || 'DRAFT'}
                            <StatusBadge status={inv.status} overdue={inv.overdue} />
                        </h1>
                        <p className="text-xs text-muted-foreground mt-0.5">Created {fmtDate(inv.created_at)} · {inv.customer_snapshot?.name}</p>
                    </div>
                </div>
                <div className="flex items-center gap-1 flex-wrap">
                    <Button variant="outline" size="sm" onClick={openPdf} data-testid="btn-pdf"><FileText size={14} className="mr-1" /> PDF</Button>
                    <Button variant="outline" size="sm" onClick={() => setThermalOpen(true)} data-testid="btn-thermal-print"><Printer size={14} className="mr-1" /> Print</Button>
                    {inv.doc_type === 'quote' && inv.status !== 'converted' && !isCancelled && (
                        <Button size="sm" onClick={convertQuote} disabled={converting} data-testid="btn-convert-quote">
                            <ArrowRight size={14} className="mr-1" /> {converting ? 'Converting…' : 'Convert to Invoice'}
                        </Button>
                    )}
                    <Button variant="outline" size="sm" onClick={openEmailDialog} disabled={isDraft} data-testid="btn-email"><Send size={14} className="mr-1" /> Email</Button>
                    <Button variant="outline" size="sm" onClick={shareWhatsapp} disabled={isDraft} data-testid="btn-whatsapp"><MessageCircle size={14} className="mr-1" /> WhatsApp</Button>
                    {!isDraft && !isCancelled && (
                        <Button variant="outline" size="sm" onClick={copyPayLink} disabled={genPayLink} data-testid="btn-paylink">
                            <Link2 size={14} className="mr-1" /> {genPayLink ? 'Creating…' : 'Pay Link'}
                        </Button>
                    )}
                    <Button variant="outline" size="sm" onClick={onDuplicate} data-testid="btn-duplicate"><Copy size={14} className="mr-1" /> Duplicate</Button>
                    {isDraft && <Button variant="outline" size="sm" asChild data-testid="btn-edit"><Link to={`/invoicing/invoices/${id}/edit`}><Edit size={14} className="mr-1" /> Edit</Link></Button>}
                    {isDraft && <Button size="sm" onClick={onIssue} data-testid="btn-issue"><ExternalLink size={14} className="mr-1" /> Issue</Button>}
                    {!isDraft && !isCancelled && <Button variant="outline" size="sm" onClick={() => setShowPay(true)} data-testid="btn-record-payment"><IndianRupee size={14} className="mr-1" /> Record payment</Button>}
                    {!isDraft && !isCancelled && <Button variant="outline" size="sm" onClick={() => setShowCancel(true)} className="text-red-600" data-testid="btn-cancel-invoice"><Ban size={14} className="mr-1" /> Cancel</Button>}
                    {isDraft && <Button variant="outline" size="sm" onClick={onDelete} className="text-red-600" data-testid="btn-delete"><Trash2 size={14} className="mr-1" /> Delete</Button>}
                </div>
            </div>

            {/* Sprint 2026-07: quick status strip — emailed at, pay link cached */}
            {(inv.last_emailed_at || inv.razorpay_short_url) && (
                <div className="flex items-center gap-4 flex-wrap text-xs text-muted-foreground" data-testid="status-strip">
                    {inv.last_emailed_at && (
                        <div className="flex items-center gap-1.5" data-testid="emailed-at">
                            <Check size={12} className="text-emerald-600" />
                            Emailed to <span className="font-medium text-foreground">{inv.last_emailed_to}</span>
                            <span>·</span>
                            <Clock size={12} />
                            <span>{fmtDate(inv.last_emailed_at)}</span>
                        </div>
                    )}
                    {inv.razorpay_short_url && (
                        <a
                            href={inv.razorpay_short_url}
                            target="_blank"
                            rel="noreferrer"
                            className="flex items-center gap-1.5 hover:text-foreground"
                            data-testid="paylink-status"
                        >
                            <Link2 size={12} className="text-primary" />
                            Pay link ready: <span className="font-mono text-primary">{inv.razorpay_short_url}</span>
                        </a>
                    )}
                </div>
            )}

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                <div className="lg:col-span-2 space-y-4">
                    {/* Customer */}
                    <Card>
                        <CardHeader><CardTitle className="text-base">Bill To</CardTitle></CardHeader>
                        <CardContent className="text-sm space-y-1">
                            <div className="font-semibold">{inv.customer_snapshot?.name}</div>
                            {inv.customer_snapshot?.gstin && <div className="text-xs font-mono">GSTIN: {inv.customer_snapshot.gstin}</div>}
                            <div className="text-xs text-muted-foreground">
                                {[inv.customer_snapshot?.billing_address?.line1, inv.customer_snapshot?.billing_address?.city, inv.customer_snapshot?.billing_address?.state, inv.customer_snapshot?.billing_address?.pincode].filter(Boolean).join(', ')}
                            </div>
                        </CardContent>
                    </Card>

                    {/* Line items */}
                    <Card>
                        <CardHeader><CardTitle className="text-base">Line Items</CardTitle></CardHeader>
                        <CardContent className="p-0">
                            <div className="overflow-x-auto">
                                <table className="w-full text-sm">
                                    <thead className="text-xs text-muted-foreground border-b border-border/60">
                                        <tr><th className="text-left p-3">Description</th><th className="text-left p-3">HSN</th><th className="text-right p-3">Qty</th><th className="text-right p-3">Rate</th><th className="text-right p-3">GST</th><th className="text-right p-3">Total</th></tr>
                                    </thead>
                                    <tbody>
                                        {(inv.line_items || []).map((l, i) => (
                                            <tr key={l.id || `line-${i}`} className="border-b border-border/40 last:border-0" data-testid={`detail-line-${i}`}>
                                                <td className="p-3"><div>{l.description}</div>{l.unit !== 'nos' && <div className="text-xs text-muted-foreground">Unit: {l.unit}</div>}</td>
                                                <td className="p-3 font-mono text-xs">{l.hsn_sac || '—'}</td>
                                                <td className="p-3 text-right tabular-nums">{l.quantity} {l.unit}</td>
                                                <td className="p-3 text-right tabular-nums">{fmtMoney(l.rate)}</td>
                                                <td className="p-3 text-right tabular-nums">{l.gst_rate}%</td>
                                                <td className="p-3 text-right tabular-nums font-medium">{fmtMoney(l.total)}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </CardContent>
                    </Card>

                    {/* Payments */}
                    {payments.length > 0 && (
                        <Card>
                            <CardHeader><CardTitle className="text-base">Payments Received</CardTitle></CardHeader>
                            <CardContent className="p-0">
                                <table className="w-full text-sm">
                                    <thead className="text-xs text-muted-foreground border-b"><tr><th className="text-left p-3">Date</th><th className="text-left p-3">Mode</th><th className="text-left p-3">Reference</th><th className="text-right p-3">Amount</th><th></th></tr></thead>
                                    <tbody>
                                        {payments.map((p) => (
                                            <tr key={p.id} className="border-b border-border/40 last:border-0" data-testid={`payment-row-${p.id}`}>
                                                <td className="p-3">{fmtDate(p.payment_date)}</td>
                                                <td className="p-3 capitalize">{p.mode}</td>
                                                <td className="p-3 text-xs">{p.reference || '—'}</td>
                                                <td className="p-3 text-right tabular-nums font-medium text-emerald-700">{fmtMoney(p.amount)}</td>
                                                <td className="p-3 text-right"><Button variant="ghost" size="icon" onClick={() => onDeletePayment(p.id)} data-testid={`btn-delete-payment-${p.id}`}><Trash2 size={12} /></Button></td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </CardContent>
                        </Card>
                    )}
                </div>

                {/* Right column: totals */}
                <div>
                    <Card className="sticky top-4">
                        <CardHeader><CardTitle className="text-base">Summary</CardTitle></CardHeader>
                        <CardContent className="space-y-2 text-sm">
                            <Row lbl="Subtotal">{fmtMoney(inv.subtotal_taxable)}</Row>
                            {inv.global_discount_amount > 0 && <Row lbl={`Discount (${inv.global_discount_percent}%)`}>− {fmtMoney(inv.global_discount_amount)}</Row>}
                            {inv.shipping_charges > 0 && <Row lbl="Shipping">+ {fmtMoney(inv.shipping_charges)}</Row>}
                            {inv.total_cgst > 0 && <Row lbl="CGST">+ {fmtMoney(inv.total_cgst)}</Row>}
                            {inv.total_sgst > 0 && <Row lbl="SGST">+ {fmtMoney(inv.total_sgst)}</Row>}
                            {inv.total_igst > 0 && <Row lbl="IGST">+ {fmtMoney(inv.total_igst)}</Row>}
                            {inv.round_off !== 0 && <Row lbl="Round off">{fmtMoney(inv.round_off)}</Row>}
                            <div className="border-t border-border/60 pt-2 mt-2">
                                <div className="flex items-center justify-between">
                                    <span className="font-semibold">Grand Total</span>
                                    <span className="font-heading text-xl font-semibold text-primary" data-testid="detail-grand-total">{fmtMoney(inv.grand_total)}</span>
                                </div>
                                {inv.amount_paid > 0 && (
                                    <>
                                        <div className="flex items-center justify-between text-sm mt-2"><span className="text-muted-foreground">Paid</span><span className="tabular-nums text-emerald-700">{fmtMoney(inv.amount_paid)}</span></div>
                                        <div className="flex items-center justify-between text-sm"><span className="font-medium">Amount Due</span><span className="tabular-nums font-semibold">{fmtMoney(inv.amount_due)}</span></div>
                                    </>
                                )}
                                <div className="text-xs text-muted-foreground mt-3 italic">{inv.amount_in_words}</div>
                            </div>
                        </CardContent>
                    </Card>
                </div>
            </div>

            {/* Payment dialog */}
            <Dialog open={showPay} onOpenChange={setShowPay}>
                <DialogContent data-testid="payment-dialog">
                    <DialogHeader>
                        <DialogTitle>Record Payment</DialogTitle>
                        <DialogDescription>Amount due: <strong>{fmtMoney(inv.amount_due)}</strong></DialogDescription>
                    </DialogHeader>
                    <div className="space-y-3">
                        <div><Label className="text-xs">Amount *</Label><Input data-testid="pay-amount" type="number" step="0.01" value={payForm.amount} onChange={(e) => setPayForm({ ...payForm, amount: e.target.value })} /></div>
                        <div><Label className="text-xs">Date</Label><Input data-testid="pay-date" type="date" value={payForm.payment_date} onChange={(e) => setPayForm({ ...payForm, payment_date: e.target.value })} /></div>
                        <div><Label className="text-xs">Mode</Label>
                            <Select value={payForm.mode} onValueChange={(v) => setPayForm({ ...payForm, mode: v })}>
                                <SelectTrigger data-testid="pay-mode"><SelectValue /></SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="cash">Cash</SelectItem>
                                    <SelectItem value="upi">UPI</SelectItem>
                                    <SelectItem value="bank_transfer">Bank Transfer</SelectItem>
                                    <SelectItem value="cheque">Cheque</SelectItem>
                                    <SelectItem value="card">Card</SelectItem>
                                    <SelectItem value="other">Other</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>
                        <div><Label className="text-xs">Reference</Label><Input data-testid="pay-ref" value={payForm.reference} onChange={(e) => setPayForm({ ...payForm, reference: e.target.value })} placeholder="Txn ID / Cheque #" /></div>
                        <div><Label className="text-xs">Notes</Label><Textarea data-testid="pay-notes" rows={2} value={payForm.notes} onChange={(e) => setPayForm({ ...payForm, notes: e.target.value })} /></div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setShowPay(false)}>Cancel</Button>
                        <Button onClick={onRecordPayment} data-testid="pay-submit">Record Payment</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Cancel dialog */}
            <Dialog open={showCancel} onOpenChange={setShowCancel}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>Cancel Invoice</DialogTitle>
                        <DialogDescription>The invoice number will be preserved (GST requirement). Please note the reason.</DialogDescription>
                    </DialogHeader>
                    <Textarea data-testid="cancel-reason" rows={3} value={cancelReason} onChange={(e) => setCancelReason(e.target.value)} placeholder="Reason for cancellation" />
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setShowCancel(false)}>Back</Button>
                        <Button variant="destructive" onClick={onCancel} data-testid="cancel-confirm">Confirm Cancel</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Sprint 2026-07: Send-email dialog. Server-side sends the PDF as
                an attachment using the workspace's SMTP config. */}
            <Dialog open={showEmail} onOpenChange={setShowEmail}>
                <DialogContent className="sm:max-w-lg" data-testid="email-dialog">
                    <DialogHeader>
                        <DialogTitle>Send invoice by email</DialogTitle>
                        <DialogDescription>
                            The invoice PDF (with UPI QR + payment link if configured) will be attached automatically.
                        </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-3">
                        <div>
                            <Label className="text-xs text-muted-foreground">To</Label>
                            <Input
                                data-testid="email-to"
                                type="email"
                                value={emailForm.to}
                                onChange={(e) => setEmailForm({ ...emailForm, to: e.target.value })}
                                placeholder="customer@example.com"
                            />
                        </div>
                        <div>
                            <Label className="text-xs text-muted-foreground">CC (optional)</Label>
                            <Input
                                data-testid="email-cc"
                                type="email"
                                value={emailForm.cc}
                                onChange={(e) => setEmailForm({ ...emailForm, cc: e.target.value })}
                                placeholder="you@yourcompany.com"
                            />
                        </div>
                        <div>
                            <Label className="text-xs text-muted-foreground">Subject</Label>
                            <Input
                                data-testid="email-subject"
                                value={emailForm.subject}
                                onChange={(e) => setEmailForm({ ...emailForm, subject: e.target.value })}
                            />
                        </div>
                        <div>
                            <Label className="text-xs text-muted-foreground">Message</Label>
                            <Textarea
                                data-testid="email-body"
                                rows={5}
                                value={emailForm.body}
                                onChange={(e) => setEmailForm({ ...emailForm, body: e.target.value })}
                            />
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setShowEmail(false)} disabled={sendingEmail}>Cancel</Button>
                        <Button onClick={sendEmail} disabled={sendingEmail} data-testid="email-send">
                            <Send size={14} className="mr-1.5" /> {sendingEmail ? 'Sending…' : 'Send email'}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Sprint 2026-09: Thermal print dialog for 58 mm / 80 mm ESC/POS. */}
            <ThermalPrintDialog
                open={thermalOpen}
                onOpenChange={setThermalOpen}
                invoiceId={id}
                invoiceNumber={inv?.invoice_number}
            />
        </div>
    );
}

// ---------------------------------------------------------------
// ThermalPrintDialog (Sprint 2026-09)
// ---------------------------------------------------------------
// Fetches the ESC/POS payload from /invoices/:id/thermal, then hands it
// to the OS via the best available channel:
//   1. Android RawBT app (rawbt:base64… deeplink) — 95% of Indian
//      shopkeepers who print via BT from a phone
//   2. WebUSB — desktop Chrome with a USB thermal printer plugged in
//   3. Web Bluetooth — desktop Chrome with a BT thermal
// Falls back to a friendly "install RawBT" hint if none is available.
function ThermalPrintDialog({ open, onOpenChange, invoiceId, invoiceNumber }) {
    const [paper, setPaper] = useState(() => {
        try { return Number(localStorage.getItem('ebm_thermal_paper') || 80); }
        catch (e) { return 80; }
    });
    const [loading, setLoading] = useState(false);
    const [payload, setPayload] = useState(null);   // { base64, rawbt_url, bytes_len }

    // Fetch a fresh payload whenever the dialog opens (or paper width changes).
    useEffect(() => {
        if (!open || !invoiceId) return;
        let cancelled = false;
        (async () => {
            try {
                setLoading(true);
                setPayload(null);
                const p = await api.invThermalPayload(invoiceId, paper);
                if (!cancelled) setPayload(p);
            } catch (e) {
                if (!cancelled) toast.error('Could not build thermal payload', {
                    description: e?.response?.data?.detail || String(e),
                });
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => { cancelled = true; };
    }, [open, invoiceId, paper]);

    useEffect(() => {
        try { localStorage.setItem('ebm_thermal_paper', String(paper)); }
        catch (e) { /* ignore */ }
    }, [paper]);

    const isAndroid = typeof navigator !== 'undefined' && /android/i.test(navigator.userAgent || '');
    const hasWebUSB = typeof navigator !== 'undefined' && !!navigator.usb;
    const hasWebBT = typeof navigator !== 'undefined' && !!navigator.bluetooth;

    function handleRawBT() {
        if (!payload?.rawbt_url) return;
        // Fire the intent. If RawBT isn't installed the browser will
        // typically show a "no app can handle this" message; we surface
        // an install hint just below the button in case that happens.
        window.location.href = payload.rawbt_url;
    }

    async function handleWebUSB() {
        if (!payload?.base64 || !navigator.usb) return;
        try {
            const bytes = Uint8Array.from(atob(payload.base64), (c) => c.charCodeAt(0));
            // Filter for a broad set of ESC/POS vendor IDs. Users can
            // override in a future settings screen if they own an
            // unusual OEM printer.
            const device = await navigator.usb.requestDevice({
                filters: [
                    { vendorId: 0x04b8 }, // Epson
                    { vendorId: 0x0483 }, // ST/other
                    { vendorId: 0x0416 }, // Winbond
                    { vendorId: 0x0525 }, // Xprinter / TVSE
                    { vendorId: 0x0dd4 }, // Custom
                    { vendorId: 0x1a86 }, // QinHeng CH34x
                    { vendorId: 0x28e9 }, // Bixolon
                    { vendorId: 0x1cbe }, // Everycom
                ],
            });
            await device.open();
            if (device.configuration === null) await device.selectConfiguration(1);
            await device.claimInterface(device.configuration.interfaces[0].interfaceNumber);
            // Endpoint 1 (OUT) is standard for USB printers.
            await device.transferOut(1, bytes);
            toast.success('Sent to printer');
            onOpenChange?.(false);
        } catch (e) {
            toast.error('WebUSB print failed', { description: String(e?.message || e) });
        }
    }

    async function handleWebBluetooth() {
        if (!payload?.base64 || !navigator.bluetooth) return;
        try {
            const bytes = Uint8Array.from(atob(payload.base64), (c) => c.charCodeAt(0));
            const device = await navigator.bluetooth.requestDevice({
                acceptAllDevices: true,
                // Most ESC/POS BT printers expose the "Serial Port" service.
                optionalServices: ['000018f0-0000-1000-8000-00805f9b34fb', '0000ff00-0000-1000-8000-00805f9b34fb'],
            });
            const server = await device.gatt.connect();
            const service = await server.getPrimaryService('000018f0-0000-1000-8000-00805f9b34fb')
                .catch(() => server.getPrimaryService('0000ff00-0000-1000-8000-00805f9b34fb'));
            const characteristics = await service.getCharacteristics();
            // Pick the first writable one.
            const chr = characteristics.find((c) => c.properties.writeWithoutResponse) || characteristics[0];
            // Chunk to 128B to avoid BT MTU issues.
            const chunkSize = 128;
            for (let i = 0; i < bytes.length; i += chunkSize) {
                await chr.writeValueWithoutResponse(bytes.slice(i, i + chunkSize));
            }
            toast.success('Sent to printer');
            onOpenChange?.(false);
        } catch (e) {
            toast.error('Bluetooth print failed', { description: String(e?.message || e) });
        }
    }

    function handleDownload() {
        if (!payload?.base64) return;
        const bytes = Uint8Array.from(atob(payload.base64), (c) => c.charCodeAt(0));
        const blob = new Blob([bytes], { type: 'application/octet-stream' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `thermal-${invoiceNumber || invoiceId}.bin`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        setTimeout(() => URL.revokeObjectURL(url), 60000);
    }

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-md" data-testid="thermal-dialog">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                        <Printer size={18} className="text-primary" />
                        Print thermal receipt
                    </DialogTitle>
                    <DialogDescription>
                        Send this bill to a 58 mm / 80 mm thermal printer via your phone (RawBT app) or direct USB / Bluetooth on desktop Chrome.
                    </DialogDescription>
                </DialogHeader>
                <div className="space-y-4 py-2">
                    <div>
                        <Label className="text-xs text-muted-foreground uppercase tracking-wider">Paper width</Label>
                        <div className="flex gap-2 mt-1">
                            {[80, 58].map((w) => (
                                <button
                                    key={w}
                                    type="button"
                                    onClick={() => setPaper(w)}
                                    className={
                                        'flex-1 rounded-md border px-3 py-2 text-sm font-medium transition-colors ' +
                                        (paper === w
                                            ? 'border-primary bg-primary/10 text-primary'
                                            : 'border-border hover:bg-muted')
                                    }
                                    data-testid={`thermal-paper-${w}`}
                                >
                                    {w} mm
                                </button>
                            ))}
                        </div>
                    </div>
                    <div className="text-[11px] text-muted-foreground text-center">
                        {loading ? 'Preparing…' : (payload ? `${payload.bytes_len} bytes ready to send` : 'Building payload…')}
                    </div>

                    {isAndroid ? (
                        <Button className="w-full" size="lg" onClick={handleRawBT} disabled={!payload || loading} data-testid="thermal-rawbt-btn">
                            <Smartphone size={16} className="mr-2" /> Print via RawBT (Android)
                        </Button>
                    ) : (
                        <>
                            {hasWebUSB && (
                                <Button variant="outline" className="w-full" onClick={handleWebUSB} disabled={!payload || loading} data-testid="thermal-webusb-btn">
                                    <Printer size={16} className="mr-2" /> Print via USB (WebUSB)
                                </Button>
                            )}
                            {hasWebBT && (
                                <Button variant="outline" className="w-full" onClick={handleWebBluetooth} disabled={!payload || loading} data-testid="thermal-webbt-btn">
                                    <Bluetooth size={16} className="mr-2" /> Print via Bluetooth
                                </Button>
                            )}
                            {!hasWebUSB && !hasWebBT && (
                                <div className="rounded-md border border-amber-200 bg-amber-50/60 dark:bg-amber-900/20 dark:border-amber-800 p-3 text-xs text-amber-900 dark:text-amber-100">
                                    Your browser doesn't support WebUSB/Web Bluetooth. Open Ease My Bill in Chrome or Edge on your Android phone and install <a className="underline font-medium" href="https://play.google.com/store/apps/details?id=ru.a402d.rawbtprinter" target="_blank" rel="noopener noreferrer">RawBT</a> for one-tap thermal printing.
                                </div>
                            )}
                        </>
                    )}

                    <Button variant="ghost" size="sm" className="w-full text-xs" onClick={handleDownload} disabled={!payload} data-testid="thermal-download-btn">
                        Or download the raw .bin file
                    </Button>
                </div>
            </DialogContent>
        </Dialog>
    );
}

function Row({ lbl, children }) {
    return (
        <div className="flex items-center justify-between">
            <span className="text-muted-foreground">{lbl}</span>
            <span className="tabular-nums">{children}</span>
        </div>
    );
}
